"use client";

import { startTransition, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { BookOpenIcon, ClipboardCheckIcon, ContactRoundIcon, SettingsIcon, UsersIcon, VoteIcon } from "lucide-react";
import { api } from "@/lib/api";
import { isAdmin, isParentRole, isStaff } from "@/lib/roles";
import { cn } from "@/lib/utils";
import { StatusScreen } from "@/components/status-screen";
import { useMeContext } from "@/components/me-provider";
import { TabScreen } from "@/components/page-views";

type NavItem = {
  href: string;
  label: string;
  icon: typeof ClipboardCheckIcon;
  badge: boolean;
};

function currentTabPath(pathname: string) {
  if (pathname === "/today") return "/";
  if (pathname === "/homework") return "/schedule";
  return pathname;
}

function tabIndex(nav: NavItem[], pathname: string) {
  const path = currentTabPath(pathname);
  const exact = nav.findIndex((item) => item.href === path);
  if (exact >= 0) return exact;
  return nav.findIndex((item) => item.href !== "/" && path.startsWith(item.href));
}

function neighborHrefs(nav: NavItem[], index: number) {
  return [index - 1, index, index + 1]
    .filter((item) => item >= 0 && item < nav.length)
    .map((item) => nav[item].href);
}

function isSwipeBlocked(target: EventTarget | null) {
  if (typeof document !== "undefined" && document.querySelector('[aria-modal="true"]')) {
    return true;
  }
  if (!(target instanceof Element)) return true;
  if (target.closest("input, textarea, select, [contenteditable='true'], [role='dialog'], [data-slot='dialog-content']")) {
    return true;
  }
  let node: HTMLElement | null = target instanceof HTMLElement ? target : target.parentElement;
  while (node) {
    const { overflowX } = getComputedStyle(node);
    if ((overflowX === "auto" || overflowX === "scroll") && node.scrollWidth > node.clientWidth + 8) {
      return true;
    }
    node = node.parentElement;
  }
  return false;
}

export function AppShell({
  children,
}: {
  children: React.ReactNode;
}) {
  const me = useMeContext();
  const pathname = usePathname();
  const router = useRouter();
  const staff = isStaff(me.user.role);
  const admin = isAdmin(me.user.role);
  const path = currentTabPath(pathname);

  const nav = useMemo<NavItem[]>(() => {
    if (admin) {
      return [
        { href: "/", label: "Сьогодні", icon: ClipboardCheckIcon, badge: Boolean(me.badges?.today) },
        { href: "/schedule", label: "Щоденник", icon: BookOpenIcon, badge: Boolean(me.badges?.homework) },
        { href: "/votes", label: "Голоси", icon: VoteIcon, badge: Boolean(me.badges?.votes) },
        { href: "/kids", label: "Клас", icon: UsersIcon, badge: false },
        { href: "/people", label: "Люди", icon: ContactRoundIcon, badge: Boolean(me.badges?.people) },
        { href: "/settings", label: "Налашт.", icon: SettingsIcon, badge: false },
      ];
    }
    if (staff || isParentRole(me.user.role)) {
      return [
        { href: "/", label: "Сьогодні", icon: ClipboardCheckIcon, badge: Boolean(me.badges?.today) },
        { href: "/schedule", label: "Щоденник", icon: BookOpenIcon, badge: Boolean(me.badges?.homework) },
        { href: "/votes", label: "Голоси", icon: VoteIcon, badge: Boolean(me.badges?.votes) },
        { href: "/kids", label: "Клас", icon: UsersIcon, badge: false },
      ];
    }
    return [];
  }, [admin, staff, me.user.role, me.badges]);

  const pathIndex = Math.max(0, tabIndex(nav, pathname));
  const [visualIndex, setVisualIndex] = useState(pathIndex);
  const viewportRef = useRef<HTMLDivElement>(null);
  const paneRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const tabScrollRef = useRef(new Map<string, number>());
  const visualRef = useRef(visualIndex);
  const navRef = useRef(nav);
  const animatingRef = useRef(false);
  const replaceTimerRef = useRef(0);
  visualRef.current = visualIndex;
  navRef.current = nav;

  const [mounted, setMounted] = useState(() => new Set(neighborHrefs(nav, pathIndex)));

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setMounted(new Set(navRef.current.map((item) => item.href)));
    }, 350);
    return () => window.clearTimeout(timer);
  }, []);

  function paneWidth() {
    return paneRef.current?.clientWidth || viewportRef.current?.clientWidth || 1;
  }

  function paint(index: number, offset: number, animate: boolean) {
    const track = trackRef.current;
    if (!track) return;
    animatingRef.current = animate;
    track.style.transition = animate ? "transform 300ms cubic-bezier(0.32, 0.72, 0, 1)" : "none";
    track.style.transform = `translate3d(${-index * paneWidth() + offset}px, 0, 0)`;
    if (paneRef.current) paneRef.current.scrollLeft = 0;
  }

  const goToIndexRef = useRef<(next: number) => void>(() => undefined);

  function goToIndex(next: number) {
    const item = nav[next];
    if (!item || next === visualRef.current) return;
    setMounted((current) => {
      const merged = new Set(current);
      for (const href of neighborHrefs(nav, next)) merged.add(href);
      return merged;
    });
    paint(next, 0, next !== visualRef.current);
    visualRef.current = next;
    setVisualIndex(next);
    requestAnimationFrame(() => {
      const pane = viewportRef.current?.querySelector(`[data-tab-scroll="${item.href}"]`);
      if (pane instanceof HTMLElement) {
        pane.scrollTop = tabScrollRef.current.get(item.href) ?? 0;
      }
    });
    window.clearTimeout(replaceTimerRef.current);
    if (currentTabPath(pathname) === item.href) return;
    replaceTimerRef.current = window.setTimeout(() => {
      animatingRef.current = false;
      startTransition(() => {
        router.replace(item.href, { scroll: false });
      });
    }, 320);
  }
  goToIndexRef.current = goToIndex;

  useEffect(() => {
    const next = Math.max(0, tabIndex(navRef.current, pathname));
    if (next !== visualRef.current && !animatingRef.current) {
      visualRef.current = next;
      setVisualIndex(next);
      paint(next, 0, false);
    }
    setMounted((current) => {
      const merged = new Set(current);
      for (const href of neighborHrefs(navRef.current, next)) merged.add(href);
      return merged;
    });
  }, [pathname]);

  useLayoutEffect(() => {
    paint(visualRef.current, 0, false);
    const pane = paneRef.current;
    if (!pane) return;
    const observer = new ResizeObserver(() => {
      if (!animatingRef.current) paint(visualRef.current, 0, false);
    });
    observer.observe(pane);
    return () => observer.disconnect();
  }, [nav.length]);

  useLayoutEffect(() => {
    const host = viewportRef.current;
    if (!host || navRef.current.length < 2) return;
    const surface = host;

    let startX = 0;
    let startY = 0;
    let lastX = 0;
    let lastT = 0;
    let velocity = 0;
    let axis: "undecided" | "x" | "y" = "undecided";
    let pointerId: number | null = null;
    let source: "none" | "pointer" | "touch" = "none";

    function rubber(index: number, dx: number) {
      const atStart = index <= 0 && dx > 0;
      const atEnd = index >= navRef.current.length - 1 && dx < 0;
      if (!atStart && !atEnd) return dx;
      const width = paneWidth();
      return (dx * width) / (width + Math.abs(dx) * 1.35);
    }

    function finish(dx: number) {
      if (source === "none" || axis !== "x") {
        source = "none";
        axis = "undecided";
        paint(visualRef.current, 0, true);
        return;
      }
      source = "none";
      axis = "undecided";
      const width = paneWidth();
      const index = visualRef.current;
      const count = navRef.current.length;
      const projected = dx + velocity * 180;
      const threshold = Math.max(40, width * 0.18);
      let next = index;
      if (projected <= -threshold && index < count - 1) next = index + 1;
      else if (projected >= threshold && index > 0) next = index - 1;
      if (next === index) {
        paint(index, 0, true);
        window.setTimeout(() => {
          animatingRef.current = false;
        }, 320);
        return;
      }
      goToIndexRef.current(next);
    }

    function onStart(x: number, y: number, target: EventTarget | null) {
      if (isSwipeBlocked(target)) {
        axis = "y";
        return;
      }
      if (animatingRef.current) {
        paint(visualRef.current, 0, false);
        animatingRef.current = false;
      }
      startX = x;
      startY = y;
      lastX = x;
      lastT = performance.now();
      velocity = 0;
      axis = "undecided";
    }

    function onMove(x: number, y: number, event: Event) {
      if (axis === "y") return;
      const dx = x - startX;
      const dy = y - startY;
      if (axis === "undecided") {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
        if (axis === "y") return;
      }
      event.preventDefault();
      const now = performance.now();
      const dt = now - lastT;
      if (dt > 0) velocity = (x - lastX) / dt;
      lastX = x;
      lastT = now;
      paint(visualRef.current, rubber(visualRef.current, dx), false);
    }

    function onTouchStart(event: TouchEvent) {
      if (source === "pointer") return;
      if (event.touches.length !== 1) {
        axis = "y";
        return;
      }
      source = "touch";
      onStart(event.touches[0].clientX, event.touches[0].clientY, event.target);
    }

    function onTouchMove(event: TouchEvent) {
      if (source !== "touch" || event.touches.length !== 1) return;
      onMove(event.touches[0].clientX, event.touches[0].clientY, event);
    }

    function onTouchEnd(event: TouchEvent) {
      if (source !== "touch") return;
      const dx = event.changedTouches[0].clientX - startX;
      finish(dx);
    }

    function onTouchCancel() {
      if (source !== "touch") return;
      source = "none";
      axis = "undecided";
      paint(visualRef.current, 0, true);
    }

    function onPointerDown(event: PointerEvent) {
      if (event.pointerType === "touch") return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      pointerId = event.pointerId;
      source = "pointer";
      onStart(event.clientX, event.clientY, event.target);
    }

    function onPointerMove(event: PointerEvent) {
      if (source !== "pointer" || pointerId !== event.pointerId) return;
      onMove(event.clientX, event.clientY, event);
      if (axis === "x") {
        try {
          surface.setPointerCapture(event.pointerId);
        } catch {
          // Some WebViews reject capture.
        }
      }
    }

    function onPointerUp(event: PointerEvent) {
      if (source !== "pointer" || pointerId !== event.pointerId) return;
      pointerId = null;
      const dx = event.clientX - startX;
      finish(dx);
    }

    function onPointerCancel() {
      if (source !== "pointer") return;
      pointerId = null;
      source = "none";
      axis = "undecided";
      paint(visualRef.current, 0, true);
    }

    const touchOpts = { capture: true, passive: false } as const;
    surface.addEventListener("touchstart", onTouchStart, touchOpts);
    surface.addEventListener("touchmove", onTouchMove, touchOpts);
    surface.addEventListener("touchend", onTouchEnd, { capture: true, passive: true });
    surface.addEventListener("touchcancel", onTouchCancel, { capture: true, passive: true });
    surface.addEventListener("pointerdown", onPointerDown);
    surface.addEventListener("pointermove", onPointerMove);
    surface.addEventListener("pointerup", onPointerUp);
    surface.addEventListener("pointercancel", onPointerCancel);
    return () => {
      surface.removeEventListener("touchstart", onTouchStart, touchOpts);
      surface.removeEventListener("touchmove", onTouchMove, touchOpts);
      surface.removeEventListener("touchend", onTouchEnd, true);
      surface.removeEventListener("touchcancel", onTouchCancel, true);
      surface.removeEventListener("pointerdown", onPointerDown);
      surface.removeEventListener("pointermove", onPointerMove);
      surface.removeEventListener("pointerup", onPointerUp);
      surface.removeEventListener("pointercancel", onPointerCancel);
    };
  }, [nav.length]);

  async function leavePreview() {
    await api("/api/impersonate", {
      method: "POST",
      body: JSON.stringify({ reset: true }),
    });
    window.location.assign("/");
  }

  const activeHref = nav[visualIndex]?.href ?? path;

  if (me.user.isGuest) {
    return (
      <div className="app-frame">
        <StatusScreen
          title="Відкрийте в Telegram"
          text="Напишіть боту @coordinator_b_bot команду /start і зайдіть у Mini App з кнопки."
        />
      </div>
    );
  }

  return (
    <div className="app-frame">
      {me.user.isDemo && !me.user.impersonatedBy ? (
        <div className="shrink-0 border-b border-border bg-primary/15 px-4 py-3">
          <p className="text-[11px] font-medium tracking-wide text-primary uppercase">
            Демо без Telegram
          </p>
          <p className="mt-1 text-sm leading-5 text-foreground">
            Ви адміністратор. Ролі й прив’язку батьків до дітей задаєте ви, у вкладці «Люди».
          </p>
        </div>
      ) : null}

      {me.user.impersonatedBy ? (
        <div className="shrink-0 border-b border-border bg-accent px-4 py-3">
          <p className="text-sm leading-5">
            Перегляд від імені <span className="font-medium">{me.user.name}</span>. Роль не
            змінюється — це лише перевірка, що бачить ця людина.
          </p>
          <button
            type="button"
            onClick={() => void leavePreview()}
            className="mt-2 text-xs font-medium text-primary"
          >
            Повернутись до адміністратора
          </button>
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col wide:flex-row">
      <div ref={viewportRef} className="order-1 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden touch-pan-y wide:order-2">
        {nav.length > 1 ? (
          <div ref={paneRef} className="min-h-0 flex-1 overflow-hidden">
            <div className="hidden">{children}</div>
            <div className="h-full overflow-hidden" style={{ width: `${nav.length * 100}%` }}>
              <div ref={trackRef} className="flex h-full min-h-0 w-full will-change-transform [backface-visibility:hidden]">
              {nav.map((item) => {
                const active = item.href === activeHref;
                const show = mounted.has(item.href);
                return (
                  <section
                    key={item.href}
                    data-tab-scroll={item.href}
                    className="h-full min-h-0 shrink-0 overflow-x-hidden overflow-y-auto overscroll-y-none px-4 pt-4 pb-3 wide:px-6 wide:pt-5 wide:pr-[max(1.5rem,var(--safe-right))]"
                    style={{ width: `${100 / nav.length}%` }}
                    aria-hidden={!active}
                    inert={!active}
                    onScroll={(event) => {
                      tabScrollRef.current.set(item.href, event.currentTarget.scrollTop);
                    }}
                  >
                    {show ? <TabScreen href={item.href} active={active} /> : null}
                  </section>
                );
              })}
              </div>
            </div>
          </div>
        ) : (
          <main className="min-h-0 flex-1 overflow-y-auto overscroll-y-none px-4 pt-4 pb-3 wide:px-6 wide:pt-5 wide:pr-[max(1.5rem,var(--safe-right))]">{children}</main>
        )}
      </div>

      {nav.length > 0 ? (
        <nav className="order-2 shrink-0 border-t border-border bg-[var(--sheet)]/95 px-2 pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur-md wide:order-1 wide:w-[calc(5.25rem+var(--safe-left))] wide:border-t-0 wide:border-r wide:px-1 wide:pt-1.5 wide:pb-[max(0.35rem,env(safe-area-inset-bottom))] wide:pl-[var(--safe-left)] wide:pr-1">
          <div className={cn(
            "grid gap-1",
            "grid-cols-[repeat(auto-fit,minmax(0,1fr))]",
            "wide:grid-cols-1 wide:auto-rows-min",
          )}>
            {nav.map((item, index) => {
              const active = item.href === activeHref;
              const Icon = item.icon;
              const showDot = item.badge && !active;
              return (
                <button
                  key={item.href}
                  type="button"
                  onClick={() => goToIndex(index)}
                  className={cn(
                    "flex flex-col items-center justify-center gap-0.5 rounded-2xl px-1 py-2 text-[11px] leading-tight font-medium transition wide:w-full wide:overflow-visible wide:px-0.5 wide:py-2 wide:text-[10px]",
                    nav.length > 5 && "px-0.5 text-[10px]",
                    active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent",
                  )}
                  aria-label={showDot ? `${item.label}, є нове` : item.label}
                >
                  <span className="relative">
                    <Icon className="size-4" />
                    {showDot ? (
                      <span className="absolute -top-0.5 -right-0.5 size-2 rounded-full bg-destructive ring-2 ring-[var(--sheet)]" />
                    ) : null}
                  </span>
                  {item.label}
                </button>
              );
            })}
          </div>
        </nav>
      ) : null}
      </div>
    </div>
  );
}
